import { Helmet } from 'react-helmet';

import { Menu } from '~/modules/elements/akui/menu';
import MenuWithLogo from '~/modules/elements/menu-with-logo';
import { NavButton, NavSwitcher } from '~/modules/elements/nav-controls';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import PhoneLinkAddressField from '~/routes/settings/phone-link-address-field';

interface Props {
  onDone: () => void;
}

/**
 * Shown once, on the first launch of the desktop app. Everything here is reachable later from
 * Settings - this screen exists because the phone link address is the one setting a fresh install
 * genuinely can't guess, and getting it wrong means the QR code points at 127.0.0.1.
 */
function DesktopSetup({ onDone }: Props) {
  const navigate = useSmoothNavigate();
  const { register } = useKeyboardNav({ title: 'Set up this computer', primary: 'start-playing' });

  return (
    <MenuWithLogo>
      <Helmet>
        <title>Setup | AllKaraoke.Party - Free Online Karaoke Party Game</title>
      </Helmet>
      <Menu.Header>Set up this computer</Menu.Header>
      <Menu.HelpText>
        Phones don&apos;t connect to this computer - they meet it on the relay server using the room code shown in the
        game. That means your friends can sing from another Wi-Fi, or from mobile data, with nothing to install.
      </Menu.HelpText>
      <KeyboardNavContext value={register}>
        <PhoneLinkAddressField
          register={register}
          name="phone-link-address"
          label="Phones open"
          remoteLabel="Phones open"
          isDefault
          info={
            <>
              The page your phone loads when it scans the QR code. It has to be an address a phone can actually reach,
              so the desktop app can&apos;t use its own - it runs on <code>127.0.0.1</code>, which on a phone means the
              phone itself.
            </>
          }
        />
        <NavSwitcher
          name="microphones"
          label="Microphones"
          info="Pick what everyone sings into - phones, real microphones, or the built-in one. You can also skip this and let the game emulate singing."
          value="Set up"
          onClick={() => navigate('select-input/')}
        />
        <NavButton name="start-playing" remoteIcon="play" onClick={onDone}>
          Start playing
        </NavButton>
      </KeyboardNavContext>
      <Menu.HelpText>You can change both of these later under Settings.</Menu.HelpText>
    </MenuWithLogo>
  );
}

export default DesktopSetup;
